import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { Fragment, ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "../../../../assets/images/icons/DeleteIcon";
import DownloadIcon from "../../../../assets/images/icons/DownloadIcon";
import DownloadIconTextToImage from "../../../../assets/images/icons/DownloadIconTextToImage";
import FullScreenIconTextToImage from "../../../../assets/images/icons/FullscreenIconTextToImage";
import FullscreenIcon from "../../../../assets/images/icons/Image/FullscreenIcon";
import TextToImageThumbdown from "../../../../assets/images/icons/TextToImageThumbDown.tsx";
import TextToImageThumbUp from "../../../../assets/images/icons/TextToImageThumbUp";

import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip";
import CopyToClipboardButton from "../../../../components/common/CopyToClipboardButton/CopyToClipboardButton";
import { FullScreenViewerProps } from "../../../../components/common/FullScreenViewer/FullScreenViewer.tsx";
import { BriaImageProps, ImageErrorType } from "../../../../components/common/Galleries/BriaImage";
import LoadingAnimation from "../../../../components/common/LoadingAnimation/LoadingAnimation";
import { useAppStore } from "../../../../hooks/useStores";
import { PlaygroundImage, PlaygroundResult } from "../../../../models/image-to-image";
import { TextToImageConfigType } from "../../../../models/text-to-image";
import { isTGApp, isTextToImageApp } from "../../../../utils/index.ts";
import iframeStore from "../../../IframeNew/iframe-store.tsx";
import { generateStructuredPromptJsonFile } from "../../../NewTG/utils/tg-utils.ts";
import PlaygroundImageOverlay from "../../Images/Overlay/PlaygroundImageOverlay";
import TextToImageGallery from "./Gallery/TextToImageGallery";
import PromptTextWithActions from "./PromptTextWithActions/PromptTextWithActions";
import styles from "./TextToImageResult.module.scss";

type Props = {
	playgroundResult: PlaygroundResult;
	fullScreenProps?: Partial<FullScreenViewerProps>;
	imageProps?: Partial<BriaImageProps & PlaygroundImage>;
	gridView?: boolean;
	hideAiEditorButton?: boolean;
	variationLabel?: string;
	extraResultInfo?: ReactElement | null;
	resultHeaderActions?: ReactElement | null;
	hideMainHeaderActions?: boolean;
	promptClassName?: string;
	hideModel?: boolean;
	showFeedbackTutorialOverlay?: boolean;
};

const AVG_RESULT_DURATION = 10;

const TextToImageResult = ({
	playgroundResult,
	gridView = false,
	fullScreenProps,
	hideAiEditorButton = false,
	variationLabel,
	extraResultInfo,
	imageProps,
	resultHeaderActions,
	hideMainHeaderActions = false,
	promptClassName,
	hideModel = false,
	showFeedbackTutorialOverlay = false,
}: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage.result" });
	const { t: modelTrans } = useTranslation("translation", { keyPrefix: "staticDropdowns.genModels" });
	const config = playgroundResult.config as TextToImageConfigType;
	const { playgroundStore, uiStore } = useAppStore();
	const [openFullScreen, setOpenFullScreen] = useState(false);
	const loading = !!playgroundResult.images.filter((image) => image.loading).length;
	const loadingDuration = AVG_RESULT_DURATION * (config.prompt_enhancement ? 1.2 : 1) * 1.1;
	const isErrorExistInImages = playgroundResult.images.every((image) => image.error !== undefined);

	const imagesWithoutError = () => {
		return playgroundResult.images.filter((image) => !image.error);
	};

	const handleDownloadImages = () => {
		const structured_prompt = playgroundResult.images?.find(
			(img: PlaygroundImage) => img?.structured_prompt,
		)?.structured_prompt;

		const additionalFiles: File[] = [
			...(structured_prompt
				? [generateStructuredPromptJsonFile(structured_prompt, `structured_prompt.json`)]
				: []),
		];

		uiStore.showExportImagePopup({
			images: imagesWithoutError(),
			additionalFiles: additionalFiles.length > 0 ? additionalFiles : undefined,
		});
	};

	const formattedVersion = config.model_version
		? config.model_version.charAt(0).toUpperCase() + config.model_version.slice(1).toLowerCase()
		: "";

	return (
		<Box
			className={clsx(styles.ttiGalleryWrapper, "galleryWrapper", {
				[styles.gridView]: gridView,
			})}
		>
			<Box className={styles.header}>
				<Box className={styles.topHeader}>
					<Box className={styles.resultInfo}>
						{!hideModel && (
							<Typography className={styles.modelVersion}>
								{variationLabel}
								{config.is_refine
									? config.model_name
										? modelTrans("tg_refiner", { name: config.model_name })
										: modelTrans("bria_fibo_refiner", { version: formattedVersion })
									: (config.model_name ?? modelTrans("bria2_3_fast"))}
							</Typography>
						)}
						{extraResultInfo && <Fragment>{extraResultInfo}</Fragment>}
					</Box>

					{!hideMainHeaderActions && (
						<>
							{loading ? (
								<LoadingAnimation
									loading={loading}
									progressBarTime={loadingDuration}
									showAnimation={false}
								/>
							) : (
								<Box className={styles.actionBtns}>
									<CopyToClipboardButton
										className={styles.iconButton}
										textToCopy={
											config.is_refine && config.refine_prompt
												? config.refine_prompt
												: config?.prompt
										}
										tooltipText={t("copyPrompt")}
									/>
									{(!iframeStore.isIframe() ||
										iframeStore.iframe.config.general_config?.enable_download ||
										iframeStore.iframe.config.general_config?.enable_save) &&
										!isErrorExistInImages && (
											<BriaTooltip title={t("download")}>
												<DownloadIcon
													className={styles.iconButton}
													onClick={handleDownloadImages}
												/>
											</BriaTooltip>
										)}
									<BriaTooltip title={t("delete")}>
										<DeleteIcon
											className={styles.iconButton}
											onClick={() => playgroundStore.deleteResult(playgroundResult.id)}
										/>
									</BriaTooltip>
									{!isErrorExistInImages && (
										<BriaTooltip title={t("fullScreen")}>
											<FullscreenIcon
												className={styles.iconButton}
												onClick={() => setOpenFullScreen(true)}
											/>
										</BriaTooltip>
									)}
								</Box>
							)}
						</>
					)}
				</Box>
				<PromptTextWithActions
					prompt={config?.prompt}
					refine_prompt={config.refine_prompt}
					is_refine={config.is_refine}
					restyle_portrait={config.restyle_portrait}
					promptClassName={promptClassName}
					resultHeaderActions={resultHeaderActions}
				/>
			</Box>
			<TextToImageGallery
				images={playgroundResult.images.map(
					(image, imageIndex) =>
						({
							ImageOverlay: (
								<PlaygroundImageOverlay
									{...{ image }}
									hideAiEditorButton={hideAiEditorButton}
									thumbUpIcon={isTextToImageApp() && !isTGApp() ? TextToImageThumbUp : undefined}
									thumbDownIcon={isTextToImageApp() && !isTGApp() ? TextToImageThumbdown : undefined}
									highlightFeedbackButtons={imageIndex === 0 && showFeedbackTutorialOverlay} // Showing the highlighted buttons
								/>
							),
							showConstantOverlay: imageIndex === 0 && showFeedbackTutorialOverlay,
							displayOverlay: "customHover",
							aspectRatio: image?.aspect_ratio,
							selectable: true,
							htmlJsonData: image.id,
							async: loading,
							onSuccessPulling: () => playgroundStore.onSuccessResult(image),
							onErrorPulling: (errorType: ImageErrorType) =>
								playgroundStore.onErrorResult(image, errorType),
							fullScreenProps: {
								fileName: playgroundStore.getFileName(image),
								format: (image.config as TextToImageConfigType)?.format,
							},
							downloadIcon: isTextToImageApp() && !isTGApp() ? DownloadIconTextToImage : undefined,
							fullScreenIcon: isTextToImageApp() && !isTGApp() ? FullScreenIconTextToImage : undefined,
							...image,
							...imageProps,
						}) as BriaImageProps & PlaygroundImage,
				)}
				fullScreenProps={{
					open: openFullScreen,
					onClose: () => setOpenFullScreen(false),
					displayMode: "scroll",
					...fullScreenProps,
				}}
				className={clsx(styles.gallery, {
					[styles.multiImages]: playgroundResult.images.length > 1 && gridView,
				})}
				gridView={gridView}
			/>
		</Box>
	);
};

export default observer(TextToImageResult);
